import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

import { Icons, IconType } from '../../../constants/icons';
import { Images } from '../../../constants/images';

import styles from './GameBlankAnswerCardsGroup.module.css';

export enum AnswerCardStatus {
  Pending = 'Pending',
  Correct = 'Correct',
  Wrong = 'Wrong',
  Empty = 'Empty',
  HintCrossed = 'HintCrossed',
  SwapPending = 'SwapPending',
}

export type AnswerCardData = {
  value: string;
  status: AnswerCardStatus;
  color?: string;
};

type GameBlankAnswerCardsGroupProps = {
  cards: AnswerCardData[];
  tutorialCard?: AnswerCardData;
  onCardClick: (card: AnswerCardData) => void;
  onUpdateCardStatus: (
    card: AnswerCardData,
    newStatus: AnswerCardStatus,
  ) => void;
};

export const GameBlankAnswerCardsGroup = ({
  cards,
  tutorialCard,
  onCardClick,
  onUpdateCardStatus,
}: GameBlankAnswerCardsGroupProps) => {
  const isCardWrappingLayout =
    cards.some((card) => card.value.length > 1) || cards.length !== 12;

  // Ref for the container
  const containerRef = useRef<HTMLDivElement | null>(null);

  // UseEffect to scroll to the tutorialCard when it changes
  useEffect(() => {
    if (tutorialCard && containerRef.current) {
      // Find the index of the tutorialCard
      const cardIndex = cards.findIndex((card) => card === tutorialCard);
      if (cardIndex !== -1) {
        // Get the corresponding child element
        const childElement = containerRef.current.children[
          cardIndex
        ] as HTMLElement;
        if (childElement) {
          // Get the relative position of the child element to the viewport
          const containerRect = containerRef.current.getBoundingClientRect();
          const childRect = childElement.getBoundingClientRect();

          // Calculate the scroll needed to center the child
          const scrollNeeded =
            childRect.top -
            containerRect.top -
            (containerRef.current.offsetHeight - childElement.offsetHeight) / 2;

          containerRef.current.scrollTo({
            top: containerRef.current.scrollTop + scrollNeeded,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [tutorialCard, cards]); // Re-run whenever tutorialCard or cards change

  return (
    <div
      ref={containerRef} // Attach the ref to the container
      className={
        isCardWrappingLayout
          ? styles.wrappingCardContainer
          : styles.cardContainer
      }
    >
      {cards.map((card, index) =>
        card.status === AnswerCardStatus.Empty ? (
          <div
            key={index}
            className={`${styles.card} ${styles.emptyCardPlaceholder}`}
            style={{ color: card.color }}
          >
            {/* MARK: value for placeholder */}
            {card.value}
          </div>
        ) : card.status === AnswerCardStatus.SwapPending ? (
          <motion.div
            key={index}
            className={`${styles.card} ${styles.swapPending} ${
              card === tutorialCard ? styles.cardWithOverlay : ''
            }`}
            style={{ color: card.color }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            onAnimationComplete={() => {
              setTimeout(() => {
                onUpdateCardStatus(card, AnswerCardStatus.Pending);
              }, 600); // Wait before updating status
            }}
          >
            {card.value}
            {card === tutorialCard &&
              card.status === AnswerCardStatus.SwapPending && (
                <motion.img
                  src={Images.HandwriteFinger}
                  alt="Finger pointing"
                  className={styles.fingerOverlay}
                  initial={{ x: 10, y: 10 }}
                  animate={{ x: [4, -4], y: [4, -4] }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    repeatType: 'reverse',
                  }}
                  style={{ pointerEvents: 'none' }}
                />
              )}
          </motion.div>
        ) : card.status === AnswerCardStatus.Correct ? (
          <motion.div
            key={index}
            className={`${styles.card} ${styles.correctCard}`}
            style={{ color: card.color }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            onAnimationComplete={() => {
              setTimeout(() => {
                onUpdateCardStatus(card, AnswerCardStatus.Empty);
              }, 600); // Wait before updating status
            }}
          >
            {card.value}
            <img
              src={Icons[IconType.TickWithOutline]}
              className={`${styles.tick} ${styles.topPosition}`}
            />
          </motion.div>
        ) : card.status === AnswerCardStatus.Wrong ? (
          <motion.div
            key={index}
            className={`${styles.card} ${styles.wrongCard}`}
            style={{ color: card.color }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            onAnimationComplete={() => {
              setTimeout(() => {
                onUpdateCardStatus(card, AnswerCardStatus.Pending);
              }, 600); // Wait before updating status
            }}
          >
            {card.value}
            {/* <SvgIcon name={SVGIconType.RedCross} className={styles.redCross} /> */}
            <img
              src={Icons[IconType.CrossWithOutline]}
              className={`${styles.redCross} ${styles.topPosition}`}
            />
          </motion.div>
        ) : card.status === AnswerCardStatus.HintCrossed ? (
          <motion.div
            key={index}
            className={`${styles.card} ${styles.eliminateCard}`}
            style={{ color: card.color }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
          >
            {card.value}
            {/* <SvgIcon name={SVGIconType.RedCross} className={styles.redCross} /> */}
            <img
              src={Icons[IconType.CrossWithOutline]}
              className={`${styles.redCross} ${styles.smallIconSize}`}
            />
          </motion.div>
        ) : (
          <motion.div
            key={index}
            className={`${styles.card} ${
              card === tutorialCard ? styles.cardWithOverlay : ''
            }`}
            style={{ color: card.color }}
            onClick={() => onCardClick(card)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.1, delay: index * 0.05 }}
          >
            {card.value}
            {card === tutorialCard &&
              card.status === AnswerCardStatus.Pending && (
                <motion.img
                  src={Images.HandwriteFinger}
                  alt="Finger pointing"
                  className={styles.fingerOverlay}
                  initial={{ x: 10, y: 10 }}
                  animate={{ x: [4, -4], y: [4, -4] }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    repeatType: 'reverse',
                  }}
                  style={{ pointerEvents: 'none' }}
                />
              )}
          </motion.div>
        ),
      )}
    </div>
  );
};
